import _ from "lodash";
import EngineHandler from "./Engines/EngineHandler";
import MappingValidations from "./Validators/MappingValidations";
import { Fields, RequiredFields } from "../shared/Fields";
import { AMGEN } from "../shared/AmgenNotice";

export default class Attachment {
  static #count = 0;

  #mappingIssuesCache = null;

  constructor({ filename, kind, data, mapping, ndcList, accessibleEntityIds, fileId }) {
    this.fileId = fileId ?? `file_${++Attachment.#count}`;
    this.filename = filename;
    this.kind = kind;
    this.data = data;
    this.mapping = mapping;
    this.mappingName = mapping?.name;

    this.originalData = [];
    this.processedData = [];
    this.summary = {};
    this.hasErrors = false;
    this.noMappingError = false;
    this.missingMappedColumns = [];
    this.columnNames = [];
    this.columnsWithErrors = [];

    this.ndcList = ndcList;
    this.accessibleEntityIds = accessibleEntityIds ?? [];
  }

  async processData(salt, onProgress) {
    const result = await EngineHandler(this.kind, {
      data: this.data,
      mapping: this.mapping?.mappings,
      ndcList: this.ndcList,
      accessibleEntityIds: this.accessibleEntityIds,
      filename: this.filename,
      salt,
      onProgress,
    });

    this.originalData = result.data.originalData;
    this.processedData = result.data.processedData;
    this.summary = result.summary;
    this.hasErrors = result.hasErrors;
    this.noMappingError = result.noMappingError;
    this.missingMappedColumns = result.missingMappedColumns || [];
    this.columnNames = result.columnNames;
    this.columnsWithErrors = result.columnsWithErrors;
    this.failedToDeIdentify = this.processedData.some((row) =>
      row.validations?.some((v) => v.message === "Data could not be de-identified")
    );
  }

  get totalRows() {
    return this.summary.original ?? 0;
  }

  get selectedRows() {
    return this.summary.rows ?? 0;
  }

  get hasValidRows() {
    return this.processedData.some((row) => !row.hasErrors);
  }

  // Rows dropped at the NDC gate only. Excludes wrong-entity rows and blocked
  // rows, which are filtered separately and counted in their own buckets.
  //
  // ENTITY FILTERING DISABLED the engines do not currently filter out wrong
  // entity rows (excludedRowCount) so this is just the NDC-filtered count.
  get excludedRows() {
    return (
      (this.summary.original ?? 0) -
      (this.summary.valid ?? 0) -
      (this.summary.invalid ?? 0) -
      (this.summary.excludedRowCount ?? 0) -
      (this.summary.blockedRowCount ?? 0)
    );
  }

  // Rows dropped for containing a suppressed place of service, HCPCS, or
  // diagnosis code value. See Processors/BlockedRowFilter.js.
  get blockedRows() {
    return this.summary.blockedRowCount ?? 0;
  }

  // ENTITY FILTERING DISABLED
  // Left these in so UI doesn't need null guarding
  // will return default as engine code for these is commented out
  get excludedEntities() {
    return this.summary.excludedEntities ?? [];
  }

  get excludedEntityRowCount() {
    return this.summary.excludedRowCount ?? 0;
  }

  get manufacturersWithErrors() {
    return Object.entries(this.summary.manufacturers ?? {}).filter(([_, stats]) => stats.hasErrors);
  }

  // Presentation flag only - Amgen rows are selected, submitted and processed like any
  // other manufacturer's. The engines key summary.manufacturers by the NDC list's
  // formatted manufacturer name, so an "Amgen" key means eligible Amgen rows are present.
  get hasAmgenNdc() {
    return Boolean(this.summary.manufacturers?.[AMGEN]);
  }

  get columnMappingIssues() {
    // Cache on the (mapping, summary) references — both are reassigned by
    // processData/reprocess, so a changed reference invalidates the cache
    const cache = this.#mappingIssuesCache;
    if (cache && cache.mapping === this.mapping && cache.summary === this.summary) {
      return cache.issues;
    }

    const mapping = this.mapping?.mappings;
    let issues = {};
    if (mapping) {
      const validate = this.#mappingValidator();
      for (const [schemaField, val] of Object.entries(mapping)) {
        if (!validate(schemaField, val))
          issues[schemaField] = {
            mappedValue: val || null,
            error: this.requiredFields.includes(schemaField) && !val,
          };
      }
    }

    // Add columns missing from file as "missing mapping" (mappedValue: null)
    if (this.missingMappedColumns?.length > 0) {
      this.missingMappedColumns.forEach(({ schemaField }) => {
        if (this.requiredFields.includes(schemaField)) {
          issues[schemaField] = {
            mappedValue: null,
            error: true,
          };
        }
      });
    }

    this.#mappingIssuesCache = { mapping: this.mapping, summary: this.summary, issues };
    return issues;
  }

  get requiredFields() {
    return RequiredFields[this.kind];
  }

  // missing mapped fields that are REQUIRED
  get mappingErrors() {
    return Object.fromEntries(
      Object.entries(this.columnMappingIssues).filter(([column, issue]) => issue.error)
    );
  }

  // Columns with mapped unexpected names (flag potential mismatch in file)
  // Columns with missing mapping but are NOT REQUIRED
  get mappingWarnings() {
    return Object.fromEntries(
      Object.entries(this.columnMappingIssues).filter(([column, issue]) => !issue.error)
    );
  }

  #mappingValidator() {
    const validators = {
      medical: (key, val) => MappingValidations.isValidMedicalColumnMapping(key, val),
      medical_encounter: (key, val) =>
        MappingValidations.isValidMedicalEncounterColumnMapping(key, val),
      pharmacy_encounter: (key, val) =>
        MappingValidations.isValidPharmacyEncounterColumnMapping(key, val),
    };

    return (
      validators[this.kind] ??
      ((key, val) => MappingValidations.isValidPharmacyColumnMapping(key, val))
    );
  }

  get mappingWarningsBySourceColumn() {
    let warnings = {};

    Object.entries(this.mappingWarnings).forEach(([schemaField, warning]) => {
      let sourceCol = warning.mappedValue;

      if (!sourceCol) return;
      if (!warnings[sourceCol]) warnings[sourceCol] = [];
      warnings[sourceCol].push({ filename: this.filename, mappedValue: schemaField });
    });

    return warnings;
  }

  get hasColumnMappingIssues() {
    return Object.keys(this.columnMappingIssues).length > 0;
  }

  get hasMappingErrors() {
    return Object.keys(this.mappingErrors).length > 0;
  }

  get hasMappingWarnings() {
    return Object.keys(this.mappingWarnings).length > 0;
  }

  get errorRowsForExport() {
    const schemaFields = _.keys(Fields[this.kind]);
    return this.originalData
      .filter((row) => row.hasErrors)
      .map((originalRow) => {
        const mapping = originalRow.mapping;
        const row = {};
        schemaFields.forEach((field) => {
          const csvColumn = mapping?.[field];
          row[field] = csvColumn ? (originalRow.data[csvColumn] ?? "") : "";
        });
        return row;
      });
  }

  get submittableRows() {
    return this.processedData.filter((row) => !row.hasErrors).map((row) => ({ ...row.data }));
  }

  get submittableRowsWithEspId() {
    const schemaFields = _.keys(Fields[this.kind]);

    return this.processedData
      .filter((row) => !row.hasErrors)
      .map((row) => {
        const data = {};
        schemaFields.forEach((field) => {
          // rx_number in row.data is the de-identified (hashed) value uploaded to the
          // server; the receipt is a local-only download that shows the real rx number.
          data[field] = field === "rx_number" ? row.rawData.rx_number : row.data[field];
        });
        return { esp_id: `CLM-${row.esp_id}`, ...data };
      });
  }
}
